import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, GraduationCap, Briefcase, BookOpen } from "lucide-react";
import { UpcomingEventsCard } from "@/components/EventsWidgets";

export default function InstitutionDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/institution/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="kb-mono text-xs text-kb-ink-muted">Loading institution dashboard…</div>;

  const stats = [
    { k: data.members_count, l: "Total Members", icon: Users },
    { k: data.students_count, l: "Students", icon: GraduationCap },
    { k: data.ideas_total, l: "Active Projects", icon: Briefcase },
    { k: data.ideas_published, l: "Published", icon: BookOpen },
  ];

  return (
    <div data-testid="institution-dashboard">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PARTNER INSTITUTION</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">{data.institution} <em className="italic text-kb-primary">workspace</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Track your members' research productivity, run workshops, and view institutional impact KPIs.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        {stats.map((s) => (
          <div key={s.l} className="bg-white p-6">
            <div className="flex items-start justify-between">
              <s.icon className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
              <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">METRIC</span>
            </div>
            <div className="font-mono text-3xl text-kb-primary mt-4">{s.k}</div>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RECENT PROJECTS</div>
          {data.recent_ideas.length === 0 ? (
            <div className="mt-4 text-sm text-kb-ink-muted">No projects from your members yet.</div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-200">
              {data.recent_ideas.map((i) => (
                <li key={i.id} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-kb-ink font-medium line-clamp-1">{i.title}</div>
                    <div className="kb-mono text-[10px] text-kb-ink-light">{i.domain} · {new Date(i.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="kb-mono text-[10px] uppercase text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm whitespace-nowrap">{i.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-5 kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MEMBERS</div>
          {data.recent_members.length === 0 ? (
            <div className="mt-4 text-sm text-kb-ink-muted">No members affiliated yet. Ask researchers to set their affiliation to <span className="font-mono text-kb-primary">{data.institution}</span>.</div>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.recent_members.map((m) => (
                <li key={m.user_id} className="flex items-center justify-between text-sm">
                  <span className="text-kb-ink truncate">{m.name}</span>
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted bg-slate-100 px-1.5 py-0.5 rounded-sm">{m.role}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8">
        <UpcomingEventsCard title="Live webinars & workshops" />
      </div>
    </div>
  );
}
